import React, { useEffect, useRef, useContext, useState, useCallback, useMemo } from 'react'
import axios from 'axios'
import { Navigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UserContext } from '../context'
import { getAccessToken } from '../services/AuthService'

/**
 * Environment:
 * - REACT_APP_BASE_URL                    -> your API base
 * - REACT_APP_PAYPAL_CLIENT_ID (SANDBOX)  -> sandbox client id for local testing
 */
const SDK_ID = 'paypal-sdk-script'

const loadPayPalSDK = (clientId) =>
  new Promise((resolve, reject) => {
    if (window.paypal) return resolve(window.paypal)
    if (document.getElementById(SDK_ID)) {
      // If script tag exists, wait a tick and resolve when paypal is available
      const check = () => (window.paypal ? resolve(window.paypal) : setTimeout(check, 50))
      return check()
    }
    const s = document.createElement('script')
    s.id = SDK_ID
    s.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription&components=buttons`
    s.async = true
    s.onload = () => (window.paypal ? resolve(window.paypal) : reject(new Error('PayPal SDK loaded but not available')))
    s.onerror = () => reject(new Error('No se pudo cargar el SDK de PayPal'))
    document.body.appendChild(s)
  })

const humanizePayPalError = (err) => {
  try {
    // Try to parse SDK structured error first
    const jd = typeof err === 'string' ? JSON.parse(err) : err
    const details = jd?.details || jd?.data?.details
    const message =
      details?.[0]?.description ||
      details?.[0]?.issue ||
      jd?.message ||
      jd?.data?.message ||
      'Ocurrió un error con PayPal.'
    return message
  } catch (_) {
    return 'Ocurrió un error con PayPal.'
  }
}

const PayPalSubscriptionButton = ({ planId, plan_id }) => {
  const [state] = useContext(UserContext)
  const [isSubmitted, setSubmitted] = useState(false)
  const [isSubSuccess, setSubSuccess] = useState(false)
  const [sdkReady, setSdkReady] = useState(!!window.paypal)
  const [creating, setCreating] = useState(false)
  const paypalRef = useRef(null)
  const callOnce = useRef(false)

  // Backward-compat with your current prop name
  const activePlanId = useMemo(() => planId || plan_id, [planId, plan_id])

  const BASE = process.env.REACT_APP_BASE_URL
  const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${getAccessToken()}` }), [])

  // Load SDK once
  useEffect(() => {
    if (sdkReady) return
    if (!CLIENT_ID) {
      toast.error('Falta REACT_APP_PAYPAL_CLIENT_ID en el entorno.')
      return
    }
    loadPayPalSDK(CLIENT_ID)
      .then(() => setSdkReady(true))
      .catch((e) => {
        console.error(e)
        toast.error(e.message || 'No se pudo cargar PayPal.')
      })
  }, [sdkReady, CLIENT_ID])

  // Call your backend to persist the subscriptionID
  const attemptApiCall = useCallback(
    async ({ subscriptionID }, attempt = 1) => {
      const retryLimit = 3
      const retryDelay = 1500
      try {
        await axios.post(
          `${BASE}/api/subscription_plan_paypal/`, // <- matches your Django PaypalSubscriptionView.post
          { subscriptionID },                 // backend binds to the authenticated user
          { headers: authHeaders(), timeout: 8000 }
        )
        setSubSuccess(true)
        setSubmitted(true)
      } catch (e) {
        if (attempt < retryLimit) {
          setTimeout(() => attemptApiCall({ subscriptionID }, attempt + 1), retryDelay)
        } else {
          console.error('API call failed:', e)
          toast.error('No se pudo guardar la suscripción. Intenta de nuevo.')
          setSubSuccess(false)
          setSubmitted(true)
        }
      }
    },
    [BASE, authHeaders]
  )

  // Render the PayPal button whenever SDK + plan are ready
  useEffect(() => {
    if (!sdkReady || !activePlanId || !paypalRef.current) return

    // Clear previous button
    paypalRef.current.innerHTML = ''
    callOnce.current = false

    const buttons = window.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe' },

      // Create the subscription on PayPal side
      createSubscription: (_data, actions) => {
        setCreating(true)
        return actions.subscription
          .create({ plan_id: activePlanId })
          .finally(() => setCreating(false))
      },

      // PayPal approved the agreement
      onApprove: ({ subscriptionID }) => {
        if (callOnce.current) return
        callOnce.current = true
        if (!subscriptionID) {
          toast.error('PayPal no devolvió un ID de suscripción.')
          setSubSuccess(false)
          setSubmitted(true)
          return
        }
        attemptApiCall({ subscriptionID })
      },

      onCancel: () => {
        setSubSuccess(false)
        setSubmitted(true)
      },

      onError: (err) => {
        callOnce.current = false
        const msg = humanizePayPalError(err)
        console.error('PayPal error:', err)
        toast.error(msg)
        setSubSuccess(false)
        setSubmitted(true)
      },
    })

    buttons.render(paypalRef.current)

    return () => {
      try { buttons.close() } catch (_) { 
        // ignore cleanup errors
      }
      if (paypalRef.current) paypalRef.current.innerHTML = ''
    }
  }, [sdkReady, activePlanId, attemptApiCall])

  // Navigate after we finish
  if (isSubmitted) {
    return isSubSuccess ? <Navigate to="/sub-success" /> : <Navigate to="/sub-cancel" />
  }

  if (!activePlanId) {
    return (
      <div className="alert alert-info" role="alert">
        Por favor selecciona un plan.
      </div>
    )
  }

  return (
    <div className="paypal-btn-wrap">
      <div ref={paypalRef} className="paypal-btn-host" />
      {creating && <div className="muted" style={{ marginTop: 8 }}>Creando suscripción…</div>}
    </div>
  )
}

export default PayPalSubscriptionButton
